/* eslint-disable @typescript-eslint/naming-convention */
import classNames from 'classnames';
import React from 'react';
import { SurveyScopeExclusion } from '../../../../../common/src/domain/surveyScopeExclusion/surveyScopeExclusionTableSchema';
import { useTranslate } from '../../../mixins/translate/translate';
import { MarkdownEditor } from '../../common/markdown/MarkdownEditor';
import { Modal } from '../../common/Modal/Modal';
import { EditPanelButton, EditPanelButtons } from '../../elements';
import { IScopePickerExclusionInput, ScopePicker } from './ScopePicker';
import { saveScope } from './useScope';

interface IEditScopeModalProps {
    surveyId: string;
    objectId: string;
    initialDefinedScope: string | null;
    initialExclusions: SurveyScopeExclusion[];
    onClose: () => void;
    onSaved: () => void;
}

type TabName = 'scope' | 'explanation';

export function EditScopeModal(props: IEditScopeModalProps): JSX.Element {
    const translate = useTranslate();
    const [activeTab, setActiveTab] = React.useState<TabName>('scope');
    const [definedScope, setDefinedScope] = React.useState<string | null>(props.initialDefinedScope);
    const [exclusions, setExclusions] = React.useState<IScopePickerExclusionInput[]>(
        props.initialExclusions.map((e) => ({ entityType: e.entityType, entityId: e.entityId })),
    );
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);

    async function handleSave() {
        setIsSaving(true);
        setSaveError(null);
        try {
            const ok = await saveScope(props.surveyId, definedScope, exclusions);
            if (ok) {
                props.onSaved();
                props.onClose();
            } else {
                // Modal stays open so the user can retry or cancel. The error
                // banner below makes the failure visible — without this the
                // button just bounces back to "Save" and the user thinks it
                // worked.
                setSaveError(translate('survey.scopeSaveFailed'));
            }
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : translate('survey.scopeSaveFailed'));
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <Modal id="edit-scope" onClose={props.onClose}>
            <Modal.Title>
                <h3>
                    <span className="panel-title">{translate('survey.scopeEditButton')}</span>
                </h3>
            </Modal.Title>
            <Modal.Content style={{ height: '500px', width: '700px' }} isScrollBarVisible={true}>
                <div style={{ width: '100%' }}>
                    <ul className="nav nav-list nav-list-topbar" style={{ marginBottom: 10, paddingLeft: 0, listStyle: 'none', display: 'flex' }}>
                        <li className={classNames('pr20', { active: activeTab === 'scope' })}>
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('scope');
                                }}
                            >
                                {translate('survey.scopeTab')}
                            </a>
                        </li>
                        <li className={classNames('pr20', { active: activeTab === 'explanation' })}>
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('explanation');
                                }}
                            >
                                {translate('survey.explanationTab')}
                            </a>
                        </li>
                    </ul>
                    {activeTab === 'scope' && (
                        <ScopePicker
                            surveyId={props.surveyId}
                            objectId={props.objectId}
                            exclusions={exclusions}
                            onChange={(next) => setExclusions(next)}
                        />
                    )}
                    {activeTab === 'explanation' && (
                        <div>
                            <MarkdownEditor
                                value={definedScope ?? ''}
                                placeholder={translate('survey.explanationPlaceholder')}
                                onChange={(value) => setDefinedScope(value === '' ? null : value)}
                            />
                        </div>
                    )}
                </div>
            </Modal.Content>
            <Modal.Actions>
                {saveError && (
                    <div className="alert alert-danger" style={{ marginBottom: 10 }}>
                        {saveError}
                    </div>
                )}
                <EditPanelButtons fixWidth={true}>
                    <EditPanelButton small={true} text={translate('common.cancel')} onClick={props.onClose} disabled={isSaving} />
                    <EditPanelButton small={true} text={translate('common.save')} onClick={() => void handleSave()} primary disabled={isSaving} />
                </EditPanelButtons>
            </Modal.Actions>
        </Modal>
    );
}
